'use client';

import { Upload, X, FileText, Loader2, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BadgeCell } from '@/components/shared/DataTable/cells/BadgeCell';
import { DataTable } from '@/components/shared/DataTable';
import type { ColumnDefinition } from '@/components/shared/DataTable/types';
import type { DataTableTheme } from '@/components/shared/DataTable/types';
import { formatFileSize } from '@/lib/formatters';

const MAX_CSV_FILE_SIZE_LABEL = '50 MB';
const LARGE_FILE_WARN_BYTES = 5 * 1024 * 1024;
const LARGE_ROW_COUNT_WARN_THRESHOLD = 50_000;

export interface ExistingAudienceInfo {
  audienceId: number;
  listId: number;
  type: 'person' | 'company';
  name: string;
  rowCount?: number;
}

interface UploadStepProps {
  isDragging: boolean;
  selectedFile: File | null;
  csvHeaders: string[];
  csvSampleRows: string[][];
  csvTableData: Record<string, string | number>[];
  csvTableColumns: ColumnDefinition[];
  csvPreviewTheme: Partial<DataTableTheme>;
  isAnalyzing: boolean;
  error: string | null;
  existingAudience?: ExistingAudienceInfo;
  onDragEnter: React.DragEventHandler;
  onDragLeave: React.DragEventHandler;
  onDragOver: React.DragEventHandler;
  onDrop: React.DragEventHandler;
  onFileSelect: React.ChangeEventHandler<HTMLInputElement>;
  onRemoveFile: () => void;
  onBack: () => void;
  onDownloadTemplate: (type: 'person' | 'company') => void;
}

export function UploadStep({
  isDragging,
  selectedFile,
  csvHeaders,
  csvSampleRows,
  csvTableData,
  csvTableColumns,
  csvPreviewTheme,
  isAnalyzing,
  error,
  existingAudience,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onFileSelect,
  onRemoveFile,
  onBack,
  onDownloadTemplate,
}: UploadStepProps) {
  return (
    <div className="space-y-4">
      {/* Breadcrumb — fixed to use > direction */}
      <div className="flex items-center gap-1.5 text-sm">
        <button
          type="button"
          onClick={onBack}
          className="text-slate-600 hover:text-slate-900 transition-colors font-medium"
        >
          {existingAudience ? existingAudience.name : 'Add audience'}
        </button>
        <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span className="text-slate-900 font-medium">
          {existingAudience ? 'Add contacts from CSV' : 'Import from CSV'}
        </span>
      </div>

      {/* Append-mode banner */}
      {existingAudience && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start justify-between gap-3">
          <div className="text-sm text-blue-900">
            <p className="font-medium">
              Adding to &ldquo;{existingAudience.name}&rdquo;
              {typeof existingAudience.rowCount === 'number' && (
                <span className="font-normal text-blue-800">
                  {' '}— currently {existingAudience.rowCount.toLocaleString()} row
                  {existingAudience.rowCount === 1 ? '' : 's'}
                </span>
              )}
            </p>
            <p className="text-xs text-blue-800 mt-0.5">
              New rows from this CSV will be appended to the existing audience.
            </p>
          </div>
          <button
            type="button"
            onClick={onBack}
            className="text-xs text-blue-700 hover:text-blue-900 font-medium whitespace-nowrap underline-offset-2 hover:underline"
          >
            Cancel &amp; create new instead
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {!selectedFile ? (
        /* Drop zone */
        <div
          className={cn(
            'relative border-2 border-dashed rounded-lg p-8 transition-all',
            'flex flex-col items-center justify-center min-h-[200px]',
            isDragging
              ? 'border-slate-400 bg-slate-50'
              : 'border-slate-300 bg-white hover:border-slate-400',
          )}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <div className="mb-3 p-3 rounded-full bg-slate-100">
            <Upload className="w-6 h-6 text-slate-400" />
          </div>
          <div className="text-center">
            <label
              htmlFor="csv-file-input"
              role="button"
              tabIndex={0}
              aria-label="Upload CSV file"
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.currentTarget.control?.click();
                }
              }}
              className="text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
            >
              Click to upload
            </label>
            <span className="text-slate-500 ml-1">or drag and drop</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">CSV file (max {MAX_CSV_FILE_SIZE_LABEL})</p>
          <div className="mt-3 flex items-center gap-3 text-xs">
            <span className="text-slate-500">Not sure what columns to include?</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDownloadTemplate(existingAudience?.type ?? 'person');
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Download CSV template
            </button>
          </div>
          <input id="csv-file-input" type="file" accept=".csv" onChange={onFileSelect} className="hidden" />
        </div>
      ) : (
        /* File selected — info card + preview */
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex-shrink-0 w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{selectedFile.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <BadgeCell value={`${csvSampleRows.length} rows`} variant="secondary" />
                    <BadgeCell value={formatFileSize(selectedFile.size)} variant="outline" />
                    {isAnalyzing && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Analyzing in background…
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <label
                  htmlFor="csv-file-input-replace"
                  className="px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-md cursor-pointer transition-colors"
                >
                  Choose different file
                </label>
                <button
                  type="button"
                  onClick={onRemoveFile}
                  className="p-1.5 hover:bg-slate-200 rounded transition-colors"
                  aria-label="Remove file"
                >
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              </div>
            </div>
          </div>

          {/* Large file warning — only shown when size heuristic suggests >50k rows */}
          {csvSampleRows.length >= 10 && selectedFile.size >= LARGE_FILE_WARN_BYTES && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-900">
                <span className="font-medium">Large file detected.</span> Files near {MAX_CSV_FILE_SIZE_LABEL}{' '}
                or with more than ~{LARGE_ROW_COUNT_WARN_THRESHOLD.toLocaleString()} rows may take longer to
                upload. Consider splitting the file if you run into issues.
              </p>
            </div>
          )}

          {csvHeaders.length > 0 && csvTableData.length > 0 && (
            <DataTable
              data={csvTableData}
              columns={csvTableColumns}
              rowKey="_id"
              density="compact"
              stickyHeader={false}
              stickyActions={false}
              pagination={{ mode: 'client', pageSize: 10, total: csvTableData.length }}
              theme={csvPreviewTheme}
            />
          )}

          <input
            id="csv-file-input-replace"
            type="file"
            accept=".csv"
            onChange={onFileSelect}
            className="hidden"
          />
        </div>
      )}
    </div>
  );
}
